/**
 * Servicio de exportación a CSV
 */

interface ExportColumn<T> {
  key: keyof T | string
  header: string
  accessor?: (row: T) => string | number | null | undefined
}

/**
 * Exporta datos a CSV y descarga el archivo
 */
export function exportToCSV<T extends object>(
  data: T[],
  columns: ExportColumn<T>[],
  filename: string
): void {
  if (data.length === 0) {
    console.warn('No hay datos para exportar')
    return
  }

  // Headers
  const headers = columns.map((col) => col.header)

  // Rows
  const rows = data.map((row) =>
    columns.map((col) => {
      let value: unknown

      if (col.accessor) {
        value = col.accessor(row)
      } else {
        value = row[col.key as keyof T]
      }

      // Convertir a string y escapar
      if (value === null || value === undefined) {
        return ''
      }

      const stringValue = String(value)

      // Escapar comillas dobles y envolver en comillas si contiene punto y coma, salto de línea o comilla
      if (stringValue.includes(';') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }

      return stringValue
    })
  )

  // Construir CSV
  const csvContent = [
    headers.join(';'),
    ...rows.map((row) => row.join(';')),
  ].join('\n')

  // Crear blob con BOM para UTF-8 (para Excel)
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })

  // Descargar
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', `${filename}.csv`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Formato de moneda para exportación
 */
export function formatCurrencyForExport(value: number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return value.toFixed(2)
}

/**
 * Formato de fecha para exportación.
 * Una fecha sin hora, como "2026-11-04", se lee en la hora local para que no se corra un día.
 */
export function formatDateForExport(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  try {
    const date = new Date(dateStr.length === 10 ? `${dateStr}T00:00:00` : dateStr)
    if (isNaN(date.getTime())) {
      return ''
    }
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  } catch {
    return ''
  }
}

/**
 * Formato booleano para exportación
 */
export function formatBooleanForExport(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return ''
  return value ? 'Sí' : 'No'
}
